import React, { useEffect, useId, useState } from 'react';
import { ClipboardList, Loader } from 'lucide-react';
import Sheet from '../common/Sheet';
import SignaturePad from '../common/SignaturePad';
import { LegalGuardiansService } from '../../services/patientmr/legalGuardians.service';
import { fetchChartEntriesWithStatus } from '../../services/patientmr/charting.service';
import { describeApiError, getApiErrorCode } from '../../utils/apiErrors';
import { isApiError } from '../../dtos/common/api.dto';
import type { LegalGuardian } from '../../dtos/patientmr/legalGuardian.dto';
import {
  EndVisitSignoff,
  NO_CARE_NOTE_MIN,
  NO_CARE_REASONS,
  NoCareReason,
  SIGNATURE_UNAVAILABLE_MIN,
  SIGNER_RELATIONSHIPS,
  SignerRelationship,
} from '../../dtos/patientmr/visit.dto';

export interface EndVisitSheetProps {
  open: boolean;
  onClose: () => void;
  patientName: string;
  startedAt: string | null;
  /** Phase 18 M4: used to load the entry count and the legal guardians (signer prefill) */
  patientId?: string | null;
  visitId?: string | null;
  /** Known entry count (skips the load); null = unknown, never blocks the end */
  entryCount?: number | null;
  /** Close the sheet and go back to the chart (defaults to onClose) */
  onReviewChart?: () => void;
  /** HHAX clock-out prompt (VIS-02) */
  evvPrompt: boolean;
  clockOut: 'ok' | 'missed_prompt' | '';
  onClockOut: (v: 'ok' | 'missed_prompt') => void;
  error: string | null;
  busy: boolean;
  /** The signer (or why nobody signed) and, without entries, the no-care reason */
  onConfirm: (signoff: EndVisitSignoff) => void;
}

const bigChoice = (active: boolean) =>
  `flex min-h-[48px] flex-1 items-center justify-center rounded-lg border-2 px-3 text-base font-semibold ${active ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-800'}`;
const fieldClass = 'min-h-[48px] w-full rounded-lg border border-gray-300 bg-white px-3 text-base';

export const formatElapsed = (startIso: string | null, nowMs = Date.now()): string => {
  const ms = Date.parse(startIso || '');
  if (!Number.isFinite(ms)) return '';
  const mins = Math.max(0, Math.floor((nowMs - ms) / 60000));
  return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
};

/** A legal guardian's free-text relationship ("Mother", "Legal guardian") as a signer relationship */
export const guardianRelationship = (relationship: string | undefined): SignerRelationship => {
  const r = String(relationship || '').toLowerCase();
  if (/\b(mother|father|parent|mom|dad|stepmother|stepfather)\b/.test(r)) return 'PARENT';
  if (/\b(self|patient)\b/.test(r)) return 'PATIENT';
  if (/caregiver|nurse|aide/.test(r)) return 'CAREGIVER';
  return 'GUARDIAN';
};

/**
 * End-visit error text (Phase 18 M4): 422 CHART_EMPTY, 400 SIGNER_REQUIRED and NO_CARE_INVALID get their own
 * message; other 4xx show the server text; the rest the fallback.
 */
export const endVisitErrorMessage = (error: unknown, fallback: string): string => {
  const code = getApiErrorCode(error);
  if (code === 'CHART_EMPTY' || code === 'SIGNER_REQUIRED' || code === 'NO_CARE_INVALID') return describeApiError(error, fallback);
  if (isApiError(error) && error.statusCode >= 400 && error.statusCode < 500 && error.message) return error.message;
  return fallback;
};

const countEntries = (entries: Array<{ status?: string; invalidated?: boolean }>) =>
  entries.filter((e) => !e.invalidated && e.status !== 'INVALIDATED').length;

/**
 * End a visit (Phase 9B G15, Phase 18 M4). Full-screen on a phone with the End button in a
 * sticky footer. Shows how many chart entries the visit has (with a way back to the chart);
 * with none, the clinician either charts or records "No care provided" with a reason. The
 * signer's name (prefilled from the legal guardians) and relationship go with the signature,
 * or "Unable to sign" with a reason.
 */
const EndVisitSheet: React.FC<EndVisitSheetProps> = (p) => {
  const uid = useId();
  const [loadedCount, setLoadedCount] = useState<number | null>(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [guardians, setGuardians] = useState<LegalGuardian[]>([]);
  const [signature, setSignature] = useState<string | null>(null);
  const [signMode, setSignMode] = useState<'sign' | 'unable'>('sign');
  const [signerName, setSignerName] = useState('');
  const [relationship, setRelationship] = useState<SignerRelationship | ''>('');
  const [unableReason, setUnableReason] = useState('');
  const [noCare, setNoCare] = useState(false);
  const [noCareReason, setNoCareReason] = useState<NoCareReason | ''>('');
  const [noCareNote, setNoCareNote] = useState('');

  const { open, patientId, visitId, entryCount: knownCount } = p;

  // Fresh form each time the sheet opens
  useEffect(() => {
    if (!open) return;
    setSignature(null);
    setSignMode('sign');
    setSignerName('');
    setRelationship('');
    setUnableReason('');
    setNoCare(false);
    setNoCareReason('');
    setNoCareNote('');
    setLoadedCount(null);
  }, [open]);

  // Entry count (works offline: the chart service reads the device copy)
  useEffect(() => {
    if (!open || knownCount !== undefined || !patientId || !visitId) return undefined;
    let live = true;
    setLoadingCount(true);
    fetchChartEntriesWithStatus(patientId, visitId)
      .then((r) => { if (live) setLoadedCount(countEntries(r.chartEntries || [])); })
      .catch(() => { if (live) setLoadedCount(null); })
      .finally(() => { if (live) setLoadingCount(false); });
    return () => { live = false; };
  }, [open, patientId, visitId, knownCount]);

  // Signer prefill from the legal guardians (contact order); optional, never blocks
  useEffect(() => {
    if (!open || !patientId) return undefined;
    let live = true;
    LegalGuardiansService.list(patientId)
      .then((rows) => {
        if (!live) return;
        setGuardians(rows);
        const first = rows[0];
        if (first) {
          setSignerName((n) => n || first.name);
          setRelationship((r) => r || guardianRelationship(first.relationship));
        }
      })
      .catch(() => { if (live) setGuardians([]); });
    return () => { live = false; };
  }, [open, patientId]);

  const entryCount = knownCount !== undefined ? knownCount : loadedCount;
  const noEntries = entryCount === 0;
  const careOk = noCare
    ? Boolean(noCareReason) && noCareNote.trim().length >= NO_CARE_NOTE_MIN
    : !noEntries;
  const signerOk = signMode === 'sign'
    ? Boolean(signature) && signerName.trim().length > 0 && Boolean(relationship)
    : unableReason.trim().length >= SIGNATURE_UNAVAILABLE_MIN;
  const evvOk = !p.evvPrompt || Boolean(p.clockOut);
  const canEnd = !p.busy && careOk && signerOk && evvOk;
  const reviewChart = p.onReviewChart || p.onClose;

  const confirm = () => {
    if (!canEnd) return;
    const signoff: EndVisitSignoff = signMode === 'sign'
      ? { signature: signature || undefined, signerName: signerName.trim(), signerRelationship: relationship || undefined }
      : { signatureUnavailableReason: unableReason.trim() };
    if (noCare && noCareReason) {
      signoff.noCareReason = noCareReason;
      signoff.noCareNote = noCareNote.trim();
    }
    p.onConfirm(signoff);
  };

  const pickGuardian = (g: LegalGuardian) => {
    setSignerName(g.name);
    setRelationship(guardianRelationship(g.relationship));
  };

  const hint = !careOk
    ? (noCare ? `Choose a reason and describe what happened (at least ${NO_CARE_NOTE_MIN} characters).` : 'Chart the care you gave, or choose "No care provided".')
    : !signerOk
      ? (signMode === 'sign' ? 'Get a signature and enter who signed.' : `Say why nobody could sign (at least ${SIGNATURE_UNAVAILABLE_MIN} characters).`)
      : !evvOk ? 'Answer the HHAeXchange question.' : '';

  return (
    <Sheet
      open={p.open}
      onClose={() => !p.busy && p.onClose()}
      title="End visit"
      subtitle={`${p.patientName}${p.startedAt ? ` · ${formatElapsed(p.startedAt)}` : ''}`}
      footer={(
        <div className="space-y-2">
          {hint && <p className="text-xs text-gray-600" data-testid="end-visit-hint">{hint}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={p.onClose} disabled={p.busy} className="min-h-[48px] flex-1 rounded-lg border border-gray-300 text-base font-medium text-gray-700 md:flex-none md:px-5">Back to chart</button>
            <button
              type="button"
              onClick={confirm}
              disabled={!canEnd}
              className="flex min-h-[48px] flex-[2] items-center justify-center gap-2 rounded-lg bg-slate-800 text-base font-semibold text-white disabled:opacity-50 md:flex-none md:px-6"
            >
              {p.busy ? <><Loader size={18} className="animate-spin" />Ending…</> : 'End visit'}
            </button>
          </div>
        </div>
      )}
    >
      <div className="space-y-5">
        {p.startedAt && <p className="text-sm text-gray-600">Started {new Date(p.startedAt).toLocaleString([], { hour: 'numeric', minute: '2-digit', month: 'short', day: 'numeric' })}</p>}

        {/* M4: entry count with a way back to the chart */}
        {(entryCount !== null || loadingCount) && (
          <div className={`flex items-center gap-3 rounded-lg border p-3 text-sm ${noEntries ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-gray-200 bg-gray-50 text-gray-800'}`} data-testid="end-visit-entry-count">
            <ClipboardList size={18} className="shrink-0" aria-hidden="true" />
            <span className="flex-1">
              {loadingCount ? 'Counting chart entries…' : noEntries ? 'No chart entries for this visit yet.' : `${entryCount} chart ${entryCount === 1 ? 'entry' : 'entries'} for this visit.`}
            </span>
            <button type="button" onClick={reviewChart} disabled={p.busy} className="min-h-[40px] shrink-0 font-semibold underline">Review chart</button>
          </div>
        )}

        {noEntries && (
          <fieldset>
            <label className="flex min-h-[44px] items-center gap-2 text-sm font-medium text-gray-800">
              <input type="checkbox" checked={noCare} onChange={(e) => setNoCare(e.target.checked)} className="h-5 w-5" />
              No care provided
            </label>
            {noCare && (
              <div className="mt-2 space-y-3">
                <div>
                  <label htmlFor={`${uid}-nocare`} className="mb-1 block text-sm font-medium text-gray-700">Reason</label>
                  <select id={`${uid}-nocare`} value={noCareReason} onChange={(e) => setNoCareReason(e.target.value as NoCareReason)} className={fieldClass}>
                    <option value="">Choose a reason</option>
                    {NO_CARE_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor={`${uid}-nocare-note`} className="mb-1 block text-sm font-medium text-gray-700">What happened</label>
                  <textarea id={`${uid}-nocare-note`} value={noCareNote} onChange={(e) => setNoCareNote(e.target.value)} rows={3} maxLength={1000} className="w-full rounded-lg border border-gray-300 p-3 text-base" />
                  <p className="mt-1 text-xs text-gray-500">At least {NO_CARE_NOTE_MIN} characters. Saved to the chart as a note.</p>
                </div>
              </div>
            )}
          </fieldset>
        )}

        {p.evvPrompt && (
          <fieldset>
            <legend className="mb-2 text-sm font-medium text-gray-700">Have you clocked out on HHAeXchange?</legend>
            <div className="flex gap-2">
              <button type="button" className={bigChoice(p.clockOut === 'ok')} onClick={() => p.onClockOut('ok')} aria-pressed={p.clockOut === 'ok'}>Yes, clocked out</button>
              <button type="button" className={bigChoice(p.clockOut === 'missed_prompt')} onClick={() => p.onClockOut('missed_prompt')} aria-pressed={p.clockOut === 'missed_prompt'}>Not yet</button>
            </div>
          </fieldset>
        )}

        {/* M4: signer identity */}
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-gray-700">Patient or caregiver sign-off</legend>
          <div className="flex gap-2">
            <button type="button" className={bigChoice(signMode === 'sign')} onClick={() => setSignMode('sign')} aria-pressed={signMode === 'sign'}>Signature</button>
            <button type="button" className={bigChoice(signMode === 'unable')} onClick={() => setSignMode('unable')} aria-pressed={signMode === 'unable'}>Unable to sign</button>
          </div>
        </fieldset>

        {signMode === 'sign' ? (
          <div className="space-y-3">
            <SignaturePad label="Patient or caregiver signature" value={signature} onChange={setSignature} active={p.open} />
            {guardians.length > 1 && (
              <div className="flex flex-wrap gap-2" aria-label="Legal guardians">
                {guardians.map((g) => (
                  <button key={g.guardianId} type="button" onClick={() => pickGuardian(g)} aria-pressed={signerName === g.name}
                    className={`min-h-[40px] rounded-full border px-3 text-sm ${signerName === g.name ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-800'}`}>
                    {g.name}
                  </button>
                ))}
              </div>
            )}
            <div>
              <label htmlFor={`${uid}-signer`} className="mb-1 block text-sm font-medium text-gray-700">Signed by</label>
              <input id={`${uid}-signer`} value={signerName} onChange={(e) => setSignerName(e.target.value)} maxLength={200} autoComplete="off" className={fieldClass} />
            </div>
            <div>
              <label htmlFor={`${uid}-rel`} className="mb-1 block text-sm font-medium text-gray-700">Relationship to the patient</label>
              <select id={`${uid}-rel`} value={relationship} onChange={(e) => setRelationship(e.target.value as SignerRelationship)} className={fieldClass}>
                <option value="">Choose</option>
                {SIGNER_RELATIONSHIPS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
        ) : (
          <div>
            <label htmlFor={`${uid}-unable`} className="mb-1 block text-sm font-medium text-gray-700">Why nobody could sign</label>
            <textarea id={`${uid}-unable`} value={unableReason} onChange={(e) => setUnableReason(e.target.value)} rows={2} maxLength={500} className="w-full rounded-lg border border-gray-300 p-3 text-base" />
            <p className="mt-1 text-xs text-gray-500">At least {SIGNATURE_UNAVAILABLE_MIN} characters.</p>
          </div>
        )}

        {p.error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{p.error}</p>}
      </div>
    </Sheet>
  );
};

export default EndVisitSheet;
